import type { StarClassId } from './ids';
import type { TradeGoodId } from './market';

/**
 * The system against everyone (GDD §9). The instability index (ИНС, 0–100)
 * grows with time and with what players do; crossing 25, 50 and 75 sets off
 * a crisis, and the final crisis comes at minute 40–45 at the latest. The
 * director picks the kind of crisis from what drove the index up, weighted
 * by the star, and never looks at who is winning. Starting values for tuning.
 */
export const CRISIS_KIND_IDS = ['coronalMassEjection', 'cometShower', 'stellarPulsation'] as const;
export type CrisisKindId = (typeof CRISIS_KIND_IDS)[number];

/** What drives the index up. */
export const INSTABILITY_SOURCE_IDS = ['time', 'debris', 'asteroids', 'exotics', 'corona'] as const;
export type InstabilitySourceId = (typeof INSTABILITY_SOURCE_IDS)[number];

/** Joint projects that answer a crisis (#29). */
export const PROJECT_IDS = ['magneticShield', 'interceptorNetwork', 'stellarStabilizer'] as const;
export type ProjectId = (typeof PROJECT_IDS)[number];

export interface CrisisKindDef {
  readonly id: CrisisKindId;
  /** i18n key: `crisis.<id>.name`. */
  readonly i18nKey: `crisis.${CrisisKindId}`;
  /** The joint project that answers it. */
  readonly project: ProjectId;
  /** Only the final crisis is a pulsation; regular ones are picked by the director. */
  readonly final: boolean;
  /** Strikes one sector of the map (a direction from the star), not the whole system. */
  readonly sectored: boolean;
}

export const CRISIS_KINDS: Readonly<Record<CrisisKindId, CrisisKindDef>> = {
  // A wave from the star: ships, orbital buildings, blackout on inner orbits.
  coronalMassEjection: {
    id: 'coronalMassEjection',
    i18nKey: 'crisis.coronalMassEjection',
    project: 'magneticShield',
    final: false,
    sectored: true,
  },
  // Comets fly in from the edge along visible paths, at planets and outposts.
  cometShower: {
    id: 'cometShower',
    i18nKey: 'crisis.cometShower',
    project: 'interceptorNetwork',
    final: false,
    sectored: true,
  },
  // The final crisis: the star swells and the inner orbits burn.
  stellarPulsation: {
    id: 'stellarPulsation',
    i18nKey: 'crisis.stellarPulsation',
    project: 'stellarStabilizer',
    final: true,
    sectored: false,
  },
};

export const INSTABILITY = {
  max: 100,
  /** Index points per economic cycle from each source. */
  perCycle: {
    /** Every cycle, whatever players do. */
    time: 0.8,
    /** Per unit of metals in debris left on the map (salvage removes it). */
    debrisMetal: 0.002,
    /** Per unit mined by stations on asteroids. */
    asteroidUnit: 0.03,
    /** Per unit of exotics produced by Lagrange stations. */
    exoticsUnit: 0.12,
    /** Per finished stage of a megastructure in the corona (#30). */
    coronaStage: 1,
  },
  /** Crossing each threshold sets off a crisis. */
  thresholds: [25, 50, 75] as readonly number[],
} as const;

export const CRISIS_DIRECTOR = {
  /** Every player hears of a crisis this long before it strikes (2.5 min). */
  warningTicks: 1500,
  /**
   * The director plans this long before the general warning, so players with
   * «Звёздная метеорология» hear of it earlier (its effect is the same length).
   */
  foresightTicks: 600,
  /** The final crisis is announced to all at this tick at the latest (minute 40)... */
  finalWarningTick: 24_000,
  /** ...and no regular crisis is planned after this tick, so it strikes before the final warning. */
  lastRegularPlanTick: 21_900,
  /**
   * The final crisis comes in phases (GDD §9: the star swells a hex a phase),
   * this far apart: strikes at minutes 42.5, 47.5 and 52.5. The stabilizer is
   * built in as many stages, one against each phase; all of them save the system.
   */
  finalPhases: 3,
  finalPhaseTicks: 3000,
  /** Which crisis a source pushes towards. Debris stands in for Kessler syndrome (after MVP). */
  sourceKinds: {
    debris: 'cometShower',
    asteroids: 'cometShower',
    exotics: 'coronalMassEjection',
    corona: 'coronalMassEjection',
  } as Readonly<Record<Exclude<InstabilitySourceId, 'time'>, CrisisKindId>>,
  /** How prone each star is to each regular crisis; time splits evenly before weighting. */
  starWeights: {
    G: { coronalMassEjection: 1, cometShower: 1 },
    K: { coronalMassEjection: 0.9, cometShower: 1 },
    M: { coronalMassEjection: 1.5, cometShower: 0.8 },
    F: { coronalMassEjection: 1.3, cometShower: 1 },
    redGiant: { coronalMassEjection: 1.2, cometShower: 1.2 },
    binary: { coronalMassEjection: 1, cometShower: 1.4 },
  } as Readonly<
    Record<StarClassId, Readonly<Record<Exclude<CrisisKindId, 'stellarPulsation'>, number>>>
  >,
} as const;

export interface ProjectDef {
  readonly id: ProjectId;
  /** i18n key: `project.<id>.name`. */
  readonly i18nKey: `project.${ProjectId}`;
  /** Value one player is expected to put in, ГС: the threshold is N−1 shares, full cover N. */
  readonly share: number;
  /** Goods the project takes, valued at base price. Science and ships are always welcome. */
  readonly goods: readonly TradeGoodId[];
  /** Hexes from the star where it is built, in the crisis's direction. */
  readonly distance: 'corona' | 'edge';
  /** Prestige shared among contributors by their part, when it succeeds (#30). */
  readonly prestige: number;
}

export const PROJECTS: Readonly<Record<ProjectId, ProjectDef>> = {
  magneticShield: {
    id: 'magneticShield',
    i18nKey: 'project.magneticShield',
    share: 1500,
    goods: ['alloys', 'electronics', 'exotics'],
    distance: 'corona',
    prestige: 20,
  },
  interceptorNetwork: {
    id: 'interceptorNetwork',
    i18nKey: 'project.interceptorNetwork',
    share: 1200,
    goods: ['metals', 'alloys', 'helium3'],
    distance: 'edge',
    prestige: 20,
  },
  // One stage of the stabilizer, against one phase of the pulsation.
  stellarStabilizer: {
    id: 'stellarStabilizer',
    i18nKey: 'project.stellarStabilizer',
    share: 1600,
    goods: ['alloys', 'electronics', 'exotics', 'helium3'],
    distance: 'corona',
    prestige: 20,
  },
};

export const PROJECT_RULES = {
  /** ГС of project value per point of science given. */
  sciencePrice: 8,
  /** Below this share of one player's part a contributor is not covered first. */
  minCoverShare: 0.25,
  /** Instability taken off when a project is built in time. */
  instabilityRelief: 10,
} as const;

/**
 * What a crisis does to those it hits (GDD §9). A planetary shield halves the
 * blow to its capital, «Магнитные экраны» halve it everywhere: personal
 * measures soften it, only the project stops it.
 */
export const CRISIS_EFFECTS = {
  coronalMassEjection: {
    /** Share of each ship's full hull lost in the sector. */
    hullLoss: 0.4,
    /** Capitals in the sector run at this energy for that many cycles. */
    blackoutEnergy: 0.5,
    blackoutCycles: 2,
    /** Outposts in the sector stop for this many cycles. */
    outpostDownCycles: 2,
    populationLoss: 0,
    goodsLoss: 0,
  },
  cometShower: {
    hullLoss: 0.2,
    blackoutEnergy: 1,
    blackoutCycles: 0,
    outpostDownCycles: 4,
    populationLoss: 0.1,
    goodsLoss: 0.1,
  },
  stellarPulsation: {
    hullLoss: 0.6,
    blackoutEnergy: 0.5,
    blackoutCycles: 4,
    outpostDownCycles: 6,
    populationLoss: 0.2,
    goodsLoss: 0.1,
  },
} as const satisfies Readonly<Record<CrisisKindId, Readonly<Record<string, number>>>>;

/** A planetary shield takes this share off the blow to its own capital. */
export const SHIELD_CRISIS_FACTOR = 0.5;
